import { useEffect, useState } from 'react'
import { Header } from '../../../../components/Header'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner } from '../../../../components/Commons/Spinner/Spinner'
import GenericPromises from '../../../../api/GenericPromises'
import { PrimaryButton } from '../../../../theme/buttons'
import { GridColDef, GridRowsProp } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { Item } from '../../../../interfaces/Commons/Items/items'
import useSnackBar from '../../../../components/Commons/SnackBar/useSnackBar'
import { ErrorDisplay } from '../../../../components/Commons/Error/ErrorDisplay'
import { Box } from '@mui/material'
import DataTableSticky from '../../../../components/Tables/GridTableMaterialUI/DataTableSticky'
import { FilterTable } from '../../../../components/Filter'
import { useCompanyStore } from '../../../../store/AuthStore'
import { usePreferenceTypesStore } from '../../../../store/Preferences/PreferencesStore'
import { useGridPreferences } from '../../../../hooks/useGridPreferences'
import { PreferenceType } from '../../../../interfaces/Commons/Preferences/preferences'
import { useColumnsItems } from './columns'
import { useFormatNumber } from '../../../../hooks/useFormatNumber'

export const TableItems = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { GenericGetResource } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { setFormatNumberFromTable } = useFormatNumber();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { companyState } = useCompanyStore();
  const { columnsItems } = useColumnsItems();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const [itemsData, setItemsData] = useState<GridRowsProp>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [myPreferences, setMyPreferences] = useState({});
  const [mySortPreference, setMySortPreference] = useState([]);
  const initFilter = '/items?filter=';
  const [columns, setColumns] = useState<GridColDef<Item>[]>([]);

  const myCustomColumns: GridColDef<any>[] = [
    {
      field: 'default_item_price', headerName: t("items.fields.default_item_price"), headerClassName: 'header-grid-table', flex: 1, align: 'left', headerAlign: 'left',
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'tax_object', headerName: t("items.fields.tax_object"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return `${params.row.tax_object} - ${params.row.tax_object_description}`;
      },
    },
  ];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/items",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            let myColumns = await LoadColumnsOrder("/items", myCustomColumns, preference?.preference_value);
            setColumns(myColumns);
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/items",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/items", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/items",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/items", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsItems);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  const GetItems = () => {
    GenericGetResource(`/items`)
      .then((response) => {
        setItemsData(response.data.data);
        setIsLoadingData(false);
      });
  }

  useEffect(() => {
    if (isLoadedMyLocalPreferences && itemsData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [itemsData, isLoadedMyLocalPreferences]);

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("items.title")}`;

    const fetchData = async () => {
      try {
        await loadTablePreferences();
        GetItems();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && <div className='screen-container'>
        <Header
          title={t("items.title")}
          child={
            <div className='d-flex flex-row-reverse alignt-items-center'>
              <PrimaryButton variant='outlined' onClick={() => { navigate("add", { state: { ...location.state } }) }}>{t("generic.buttons.add")}</PrimaryButton>
              <FilterTable
                onFilterEmpty={GetItems}
                setIsLoadingDataTable={setIsLoadingData}
                setDataTable={setItemsData}
                showSnackBar={showSnackBar}
                columnsTable={columns}
                initFilter={initFilter}
                resourceRoute="/items"
              />
            </div>
          }
        />

        {isLoadingDataTable &&
          <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
            <DataTableSticky
              columns={columns}
              setColumns={setColumns}
              data={itemsData}
              entityId={"item_id"}
              entity={`Items`}
              preferences={myPreferences}
              namePreferences={"/items"}
              nameOrderColumns={"/items"}
              sortPreference={mySortPreference}
              isLoading={isLoadingData}
            />
          </Box>
        }
        <SnackbarComponent />
      </div>
      }
    </>
  )
}
